import { useId, useState } from "react";
import type { ReactNode } from "react";
import { classes } from "../../nucleo/classes";
import { ROTULO_DO_NIVEL } from "../../nucleo/senha";
import type { ForcaDaSenha } from "../../nucleo/senha";

const SEGMENTOS = [1, 2, 3, 4] as const;

export interface PropsDoCampoDeSenha {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  /**
   * `current-password` na entrada, `new-password` ao definir uma senha própria.
   *
   * `off` é para senha que **não é de quem digita** — a credencial do servidor do cliente, que
   * vai para o cofre. Sem ele, o gerenciador do navegador oferece guardar a senha do ERP alheio
   * no cofre pessoal de quem preencheu. A declaração é honesta, e a limitação também: parte dos
   * navegadores ignora `off` em campo de senha, então ela reduz o risco sem eliminá-lo.
   */
  autoComplete: "current-password" | "new-password" | "off";
  nome?: string;
  ajuda?: ReactNode;
  erro?: string;
  /**
   * Marca o campo como obrigatório — asterisco e validação nativa.
   *
   * Padrão `true` porque toda senha do fluxo de acesso (entrar, convite, redefinir) é
   * obrigatória. Existe em `false` por causa de uma pergunta real: a senha do SMTP do cliente,
   * que o catálogo declara opcional porque relay interno sem autenticação existe — e cobrar
   * senha de quem não tem produz o campo preenchido com "não tem".
   */
  obrigatorio?: boolean;
  /** Com força informada o campo mostra o medidor; sem ela, é só um campo de senha. */
  forca?: ForcaDaSenha;
  autoFocus?: boolean;
  desabilitado?: boolean;
}

/**
 * Campo de senha com revelação temporária e medidor.
 *
 * A revelação é `type="text"` no próprio input, e não um texto paralelo: assim o gerenciador
 * de senhas do navegador continua enxergando um campo só, e nada da senha é copiado para
 * outro nó do DOM.
 */
export function CampoDeSenha({
  rotulo,
  valor,
  aoMudar,
  autoComplete,
  nome,
  ajuda,
  erro,
  forca,
  obrigatorio = true,
  autoFocus = false,
  desabilitado = false,
}: PropsDoCampoDeSenha) {
  const [revelada, setRevelada] = useState(false);
  const base = useId();
  const idDoControle = `${base}-senha`;
  const idDaAjuda = `${base}-ajuda`;
  const idDoErro = `${base}-erro`;
  const idDaForca = `${base}-forca`;

  const descrito = [ajuda ? idDaAjuda : null, forca ? idDaForca : null, erro ? idDoErro : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={idDoControle}>
        {rotulo}
        {obrigatorio ? (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      <div className="campo-senha">
        <input
          id={idDoControle}
          name={nome}
          type={revelada ? "text" : "password"}
          className="campo__controle campo-senha__controle"
          value={valor}
          onChange={(evento) => aoMudar(evento.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={desabilitado}
          required={obrigatorio}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descrito || undefined}
          // Sem corretor nem capitalização: no celular eles transformam a senha em silêncio.
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="campo-senha__revelar"
          onClick={() => setRevelada((atual) => !atual)}
          aria-pressed={revelada}
          aria-controls={idDoControle}
          disabled={desabilitado}
        >
          {revelada ? "Ocultar" : "Mostrar"}
        </button>
      </div>

      {forca ? (
        <div className="medidor" id={idDaForca}>
          <div className="medidor__trilha" data-nivel={forca.nivel} aria-hidden="true">
            {SEGMENTOS.map((segmento) => (
              <span
                key={segmento}
                className={classes(
                  "medidor__marca",
                  segmento <= forca.pontos && "medidor__marca--acesa",
                )}
              />
            ))}
          </div>
          {/* O medidor é anunciado por texto, não por cor: `aria-live="polite"` avisa quem
              usa leitor de tela sem interromper a digitação. */}
          <p className="medidor__texto" data-nivel={forca.nivel} aria-live="polite">
            <span className="medidor__nivel" data-testid="nivel-da-senha">
              {valor.length === 0 ? "sem senha" : ROTULO_DO_NIVEL[forca.nivel]}
            </span>
            <span className="medidor__dica">{forca.dica}</span>
          </p>
        </div>
      ) : null}

      {ajuda ? (
        <p className="campo__ajuda" id={idDaAjuda}>
          {ajuda}
        </p>
      ) : null}

      {erro ? (
        <p className="campo__erro" id={idDoErro} role="alert">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
